import React, { useState, useEffect, useContext } from 'react';
import { StorageContext } from '../../contexts/StorageContext';
import { ProjectContext } from '../../contexts/ProjectContext';
import PromptPanel from '../PromptPanel';
import ResponsePanel from '../ResponsePanel';
import BatchSelector from '../BatchSelector';
import FileSummary from '../FileSummary';
import PromptGenerator from '../../services/PromptGenerator';
import ResponseProcessor from '../../services/ResponseProcessor';
import BatchManager from '../../services/BatchManager';
import './CodingPhase.css';

/**
 * Coding phase component
 * Handles implementing files in batches
 */
function CodingPhase() {
  const { storage } = useContext(StorageContext);
  const { project, updateProject } = useContext(ProjectContext);
  const [batches, setBatches] = useState([]);
  const [currentBatch, setCurrentBatch] = useState(null);
  const [currentBatchFiles, setCurrentBatchFiles] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [isGeneratingPrompt, setIsGeneratingPrompt] = useState(false);
  const [isProcessingResponse, setIsProcessingResponse] = useState(false);
  const [isContinuation, setIsContinuation] = useState(false);
  const [continuationInfo, setContinuationInfo] = useState(null);
  const [processingResults, setProcessingResults] = useState(null);
  const [promptGenerator] = useState(new PromptGenerator());
  const [responseProcessor] = useState(new ResponseProcessor(storage));
  const [batchManager] = useState(new BatchManager(storage));
  
  // Load batches
  useEffect(() => {
    const loadBatches = async () => {
      if (!project) return;
      
      try {
        const projectBatches = await storage.getBatchesByProject(project.id);
        setBatches(projectBatches.sort((a, b) => a.priority - b.priority));
        
        // Find first pending batch
        const pendingBatch = projectBatches.find(b => b.status === 'pending');
        if (pendingBatch) {
          setCurrentBatch(pendingBatch);
        }
      } catch (error) {
        console.error('Error loading batches:', error);
      }
    };
    
    loadBatches();
  }, [project, storage]);
  
  // Load current batch files
  useEffect(() => {
    const loadBatchFiles = async () => {
      if (!currentBatch) return;
      
      try {
        const files = [];
        
        for (const fileId of currentBatch.files) {
          const file = await storage.getFile(fileId);
          if (file) files.push(file);
        }
        
        setCurrentBatchFiles(files);
      } catch (error) {
        console.error('Error loading batch files:', error);
      }
    };
    
    loadBatchFiles();
  }, [currentBatch, storage]);
  
  // Handle batch change
  const handleBatchChange = (batchId) => {
    const batch = batches.find(b => b.id === batchId);
    if (batch) {
      setCurrentBatch(batch);
      setIsContinuation(false);
      setContinuationInfo(null);
      setProcessingResults(null);
    }
  };
  
  // Generate prompt
  const handleGeneratePrompt = async () => {
    if (!currentBatch) {
      alert('Please select a batch before generating a prompt');
      return;
    }
    
    try {
      setIsGeneratingPrompt(true);
      
      let generatedPrompt;
      if (isContinuation && continuationInfo) {
        generatedPrompt = await promptGenerator.generateContinuationPrompt(
          continuationInfo.lastCompleteLine,
          continuationInfo.filePath
        );
      } else {
        generatedPrompt = await promptGenerator.generateCodingPrompt(
          currentBatch,
          project.id
        );
      }
      
      setPrompt(generatedPrompt);
    } catch (error) {
      console.error('Error generating prompt:', error);
      alert('Failed to generate prompt');
    } finally {
      setIsGeneratingPrompt(false);
    }
  };
  
  // Process AI response
  const handleProcessResponse = async (response) => {
    try {
      setIsProcessingResponse(true);
      
      const result = await responseProcessor.processResponse(
        response,
        'CODING',
        project.id
      );
      
      if (result.success) {
        // Update batch status
        await batchManager.updateBatchStatus(currentBatch.id, 'completed');
        
        // Reload batches
        const updatedBatches = await storage.getBatchesByProject(project.id);
        setBatches(updatedBatches.sort((a, b) => a.priority - b.priority));
        
        // Find next pending batch
        const nextBatch = updatedBatches.find(b => b.status === 'pending');
        
        setCurrentBatch(nextBatch || null);
        setIsContinuation(false);
        setContinuationInfo(null);
        setProcessingResults(result);
        
        // Check if all batches are complete
        const allComplete = !updatedBatches.some(b => b.status === 'pending');
        
        if (allComplete) {
          // Update project phase
          const updatedProject = {
            ...project,
            codingComplete: true,
            updated: Date.now()
          };
          
          await storage.updateProject(updatedProject);
          updateProject(updatedProject);
        }
        
        return { success: true };
      } else if (result.truncated) {
        // Handle truncated response
        setIsContinuation(true);
        setContinuationInfo({
          lastCompleteLine: result.lastCompleteLine,
          filePath: result.filePath || (currentBatchFiles[0]?.path || 'unknown')
        });
        
        return { 
          success: false,
          error: 'Response was truncated. Please generate a continuation prompt and get the rest of the implementation.'
        };
      } else {
        return { 
          success: false,
          error: result.error || 'Failed to process response'
        };
      }
    } catch (error) {
      console.error('Error processing response:', error);
      return { 
        success: false,
        error: 'Error processing response: ' + error.message 
      };
    } finally {
      setIsProcessingResponse(false);
    }
  };
  
  // Optimize batches
  const handleOptimizeBatches = async () => {
    try {
      const optimizedBatches = await batchManager.optimizeBatches(project.id);
      setBatches(optimizedBatches.sort((a, b) => a.priority - b.priority));
      
      // Update current batch to first pending batch
      const pendingBatch = optimizedBatches.find(b => b.status === 'pending');
      if (pendingBatch) {
        setCurrentBatch(pendingBatch);
      }
    } catch (error) {
      console.error('Error optimizing batches:', error);
      alert('Failed to optimize batches');
    }
  };
  
  return (
    <div className="coding-phase">
      <div className="phase-description">
        <h2>Coding Phase</h2>
        <p>
          In this phase, you'll implement code files in batches based on the technical design.
          Each batch contains related files to be implemented together.
        </p>
      </div>
      
      <div className="batch-selection">
        <div className="batch-header">
          <h3>Implementation Batches</h3>
          <button 
            className="optimize-button" 
            onClick={handleOptimizeBatches}
            title="Reorganize batches based on dependencies"
          >
            Optimize Batches
          </button>
        </div>
        
        <BatchSelector 
          batches={batches}
          currentBatch={currentBatch?.id}
          onBatchChange={handleBatchChange}
        />
        
        {isContinuation && (
          <div className="continuation-notice">
            <h4>Continuation Mode</h4>
            <p>
              The previous response was truncated. Generate a continuation prompt to 
              get the rest of the implementation.
            </p>
            <p>
              <strong>Last Complete Line:</strong> <code>{continuationInfo?.lastCompleteLine}</code>
            </p>
            <p>
              <strong>File:</strong> <code>{continuationInfo?.filePath}</code>
            </p>
          </div>
        )}
      </div>
      
      {currentBatch && (
        <div className="current-batch-files">
          <h3>Files in Current Batch</h3>
          <div className="file-list">
            {currentBatchFiles.length > 0 ? (
              currentBatchFiles.map(file => (
                <FileSummary key={file.id} file={file} />
              ))
            ) : (
              <div className="no-files-message">No files in this batch</div>
            )}
          </div>
        </div>
      )}
      
      {processingResults && (
        <div className="processing-results">
          <h3>Last Processing Results</h3>
          <div className="results-summary">
            <p>Successfully processed {processingResults.stats?.filesProcessed || 0} files with {processingResults.stats?.totalLoc || 0} lines of code</p>
            {processingResults.files?.map(file => (
              <div key={file.id} className="processed-file">
                <span className="file-path">{file.path}</span>
                <span className="file-stats">{file.actualLoc} lines</span>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="ai-interaction">
        <PromptPanel 
          prompt={prompt}
          onGeneratePrompt={handleGeneratePrompt}
          isLoading={isGeneratingPrompt}
          title={isContinuation ? "Continuation Prompt" : "Coding Prompt"}
        />
        
        <ResponsePanel 
          onProcessResponse={handleProcessResponse}
          isProcessing={isProcessingResponse}
          title="Coding Response"
        />
      </div>
    </div>
  );
}

export default CodingPhase;
